import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CollectionForm } from "@/components/collection/collection-form";
import { CollectionHeader } from "@/components/collection/collection-header";
import { CollectionProducts } from "@/components/collection/collection-products";
import { DeleteCollectionDialog } from "@/components/collection/delete-collection-dialog";
import {
  CollectionSource,
  SyncCollectionButton,
} from "@/components/collection/sync-collection";
import { Button, buttonVariants } from "@/components/ui/button";
import GatedOverlay from "@/components/ui/gated-overlay";
import { PageLoader } from "@/components/ui/page-loader";
import {
  CollectionError,
  collectionQueryOpts,
  useDeleteCollection,
  useUpdateCollection,
} from "@/hooks/collection";
import { ebayAccountsQueryOpts } from "@/hooks/ebay";
import { isGated } from "@/hooks/gated";
import { platformsQueryOpts } from "@/hooks/platform";
import { currentUserQueryOpts } from "@/hooks/user";
import { collectionToInput, hasCollectionErrors } from "@/lib/collections";
import type { Collection, CollectionInput, Platform } from "@/lib/types";
import { getOrgIdWithFallback } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";

export const Route = createFileRoute("/$org/collections/$id")({
  loader: async ({ context, params: { id } }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);
    const params = { token: getToken() ?? "", setToken, orgId };

    const gated = await isGated(params, handleTokenInvalid);

    context.queryClient.prefetchQuery(collectionQueryOpts(id, params));
    context.queryClient.ensureQueryData(platformsQueryOpts(params));
    // the marketplaces the collection can have eBay categories for
    context.queryClient.prefetchQuery(ebayAccountsQueryOpts(params));

    return { gated: gated?.is_gated };
  },
  component: CollectionComponent,
});

function CollectionComponent() {
  const { t } = useTranslation();
  const { gated } = Route.useLoaderData();
  const { org, id } = Route.useParams();
  const { getToken, setToken } = useAuth();
  const { orgId } = useOrg();
  const params = { token: getToken() ?? "", setToken, orgId };

  const { data: collection, error } = useQuery(collectionQueryOpts(id, params));
  const { data: stores } = useQuery(platformsQueryOpts(params));
  const { data: user } = useQuery(currentUserQueryOpts(params));

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  if (error instanceof CollectionError && error.status === 404) {
    return (
      <div className="mx-auto flex max-w-4xl flex-col space-y-4">
        <CollectionHeader org={org} title={t("collections.title")} />
        <p className="text-muted-foreground">{t("collections.notFound")}</p>
        <Link
          to="/$org/collections"
          params={{ org }}
          className={buttonVariants({ variant: "outline", className: "w-fit" })}
        >
          {t("collections.back")}
        </Link>
      </div>
    );
  }

  if (!collection) {
    return <PageLoader />;
  }

  // the form starts over from the saved collection whenever it changes
  return (
    <EditCollection
      key={collection.updated_at}
      collection={collection}
      org={org}
      stores={stores ?? []}
      currency={user?.currency || "USD"}
    />
  );
}

function EditCollection({
  collection,
  org,
  stores,
  currency,
}: {
  collection: Collection;
  org: string;
  stores: Platform[];
  currency: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateCollection = useUpdateCollection(collection.id);
  const deleteCollection = useDeleteCollection();

  const [saved] = useState(() => collectionToInput(collection));
  const [input, setInput] = useState<CollectionInput>(saved);
  const [submitted, setSubmitted] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const dirty = JSON.stringify(input) !== JSON.stringify(saved);

  const save = () => {
    setSubmitted(true);
    if (hasCollectionErrors(input)) return;
    updateCollection.mutate(input);
  };

  const remove = () =>
    deleteCollection.mutate(collection.id, {
      onSuccess: () => navigate({ to: "/$org/collections", params: { org } }),
    });

  return (
    <div className="mx-auto flex max-w-4xl flex-col space-y-4">
      <CollectionHeader
        org={org}
        title={collection.title}
        description={
          collection.platform_connection_id && (
            <CollectionSource collection={collection} stores={stores} />
          )
        }
      >
        {collection.platform_connection_id && (
          <SyncCollectionButton collection={collection} stores={stores} />
        )}
        {dirty && (
          <Button
            variant="outline"
            onClick={() => setInput(saved)}
            disabled={updateCollection.isPending}
          >
            {t("common.cancel")}
          </Button>
        )}
        <Button onClick={save} disabled={!dirty || updateCollection.isPending}>
          {updateCollection.isPending
            ? t("collections.form.saving")
            : t("common.save")}
        </Button>
      </CollectionHeader>

      <CollectionForm
        value={input}
        onChange={setInput}
        isNew={false}
        stores={stores}
        showErrors={submitted}
      />

      <CollectionProducts
        collection={collection}
        currency={currency}
        org={org}
      />

      <div className="flex justify-end pb-8">
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setDeleting(true)}
        >
          {t("collections.delete.button")}
        </Button>
      </div>

      <DeleteCollectionDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={collection.title}
        onConfirm={remove}
        isPending={deleteCollection.isPending}
      />
    </div>
  );
}
